import { useState } from "react";
import { Download, Loader2, PackagePlus } from "lucide-react";
import { PageHeader, EmptyState, ErrorBox } from "@/components/common";
import { useApp } from "@/lib/store";

/** Phase 8.1 / 8.4 — ORB *scaffold* download UI (Staff only).
 *
 *  Takes a workspace slug + optional semver, then POSTs to
 *  `/orb/scaffold/` and streams the returned ZIP to disk. The endpoint
 *  enforces the `enterprise_scaffold` entitlement on the target
 *  workspace, but staff callers bypass that check server-side so this
 *  page doubles as the audit/support tool.
 *
 *  Kept intentionally simple: the scaffold produces docs, sample
 *  instances, mapping stubs and eval stubs — the heavy lifting lives
 *  in `apps.ontology.orb_scaffold.scaffold_workspace_orb`.
 */
export function AdminScaffold() {
  const user = useApp((s) => s.user);
  const workspaceId = useApp((s) => s.workspaceId);
  const workspaces = useApp((s) => s.workspaces);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const workspace = workspaces.find((item) => item.id === workspaceId);
  const canScaffold = isAdmin || Boolean(workspace?.entitlements?.includes("enterprise_scaffold"));

  const [staffWorkspaceSlug, setStaffWorkspaceSlug] = useState("");
  const [semver, setSemver] = useState("");
  const [includeShapes, setIncludeShapes] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);

  if (!canScaffold) {
    return (
      <>
        <PageHeader
          title="ORB Scaffold"
          subtitle="Generate a full project scaffold for a workspace's ontology."
        />
        <EmptyState
          title="Enterprise scaffold entitlement required"
          hint="Select an Enterprise workspace with the enterprise_scaffold entitlement."
        />
      </>
    );
  }

  const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";

  async function download() {
    const workspaceSlug = isAdmin ? staffWorkspaceSlug.trim() : workspace?.slug ?? "";
    if (!workspaceSlug.trim()) {
      setError(isAdmin ? "Enter a workspace slug." : "Select a workspace from the header.");
      return;
    }
    setBusy(true);
    setError(null);
    setLastFile(null);
    try {
      const token = localStorage.getItem("ckos.token");
      const headers = new Headers({ "Content-Type": "application/json" });
      if (token) headers.set("Authorization", `Token ${token}`);
      const slug = localStorage.getItem("ckos.tenantSchema");
      if (slug) headers.set("X-Tenant-Schema", slug);

      const body: Record<string, unknown> = {
        workspace: workspaceSlug,
        include_shapes: includeShapes,
      };
      if (semver.trim()) body.semver = semver.trim();

      const r = await fetch(`${base}/orb/scaffold/`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const ct = r.headers.get("content-type") || "";
        let detail = `${r.status} ${r.statusText}`;
        if (ct.includes("application/json")) {
          const body = await r.json();
          detail = body?.detail || detail;
          if (r.status === 402) {
            detail = `${detail} (grant 'enterprise_scaffold' to the workspace's entitlements)`;
          }
        }
        throw new Error(detail);
      }

      // Save the streamed ZIP.
      const cd = r.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const name = m ? m[1] : `${workspaceSlug}-scaffold.zip`;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLastFile(name);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="ORB Scaffold"
        subtitle="Emit a full project scaffold (orb.yaml + docs + samples + mappings + evals) for a workspace's published ontology, then download it as a zip."
      />

      <div className="card p-4 max-w-2xl space-y-4">
        <div className="flex items-start gap-3">
          <PackagePlus className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <div className="text-sm text-muted">
            The scaffold reuses the workspace's latest published
            OntologyVersion + active SHACL ShapeSets, then generates
            Markdown-per-class docs and SHACL-driven sample instances.
            Mapping and eval directories ship as stubs for the receiving
            team to fill in. Requires the <code>enterprise_scaffold</code>{" "}
            entitlement on the target workspace. Staff can generate one for
            support and audit purposes.
          </div>
        </div>

        <div className="grid gap-3">
          {isAdmin ? <label className="grid gap-1 text-sm">
            <span className="text-muted">Workspace slug</span>
            <input
              className="input"
              placeholder="acme-clinical"
              value={staffWorkspaceSlug}
              onChange={(e) => setStaffWorkspaceSlug(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </label> : (
            <div className="rounded border border-border bg-bg/60 p-3 text-sm">
              <span className="text-muted">Workspace</span>{" "}
              <strong>{workspace?.name}</strong>
              <code className="ml-2 font-mono text-xs text-muted">{workspace?.slug}</code>
            </div>
          )}
          <label className="grid gap-1 text-sm">
            <span className="text-muted">
              Ontology semver <span className="text-muted/60">(optional — defaults to latest published)</span>
            </span>
            <input
              className="input"
              placeholder="1.2.0"
              value={semver}
              onChange={(e) => setSemver(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeShapes}
              onChange={(e) => setIncludeShapes(e.target.checked)}
              disabled={busy}
            />
            <span>Include active SHACL shapes (drives sample-instance generation)</span>
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            className="btn btn-primary"
            onClick={download}
            disabled={busy || !(isAdmin ? staffWorkspaceSlug.trim() : workspace?.slug)}
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {busy ? "Scaffolding…" : "Generate & download"}
          </button>
          {lastFile && (
            <span className="text-xs text-muted">
              Downloaded <code className="font-mono">{lastFile}</code>
            </span>
          )}
        </div>

        {error && <ErrorBox error={new Error(error)} />}
      </div>
    </>
  );
}
